import { normalizePath } from '../legal/routes';

export type ContentTestingLanguage = 'en' | 'de';
export type ContentTestingCurrency = 'USD' | 'EUR';

export type ContentTestingMatch = {
  language: ContentTestingLanguage;
  currency: ContentTestingCurrency;
  path: string;
};

export const contentTestingPaths = {
  enUsd: '/content-testing',
  enEur: '/content-testing-eu',
  de: '/lerninhalte-testen',
} as const;

const PAGES: ContentTestingMatch[] = [
  { path: contentTestingPaths.enUsd, language: 'en', currency: 'USD' },
  { path: contentTestingPaths.enEur, language: 'en', currency: 'EUR' },
  { path: contentTestingPaths.de, language: 'de', currency: 'EUR' },
];

export const CONTENT_TESTING_ROUTE_PATHS: string[] = PAGES.map((page) => page.path);

export function getContentTestingFromPath(pathname: string): ContentTestingMatch | null {
  const normalized = normalizePath(pathname);
  return PAGES.find((page) => page.path === normalized) ?? null;
}

export function isContentTestingPath(pathname: string): boolean {
  return getContentTestingFromPath(pathname) !== null;
}

/** Language switch: German <-> English euro page; US English still has a German reading. */
export function getContentTestingCounterpart(pathname: string): string | null {
  const current = getContentTestingFromPath(pathname);
  if (!current) return null;
  if (current.language === 'de') return contentTestingPaths.enEur;
  return contentTestingPaths.de;
}

export function getContentTestingCurrencyCounterpart(pathname: string): string | null {
  const current = getContentTestingFromPath(pathname);
  if (!current || current.language !== 'en') return null;
  return current.currency === 'USD' ? contentTestingPaths.enEur : contentTestingPaths.enUsd;
}

export function contentTestingPathForLanguage(language: ContentTestingLanguage): string {
  return language === 'de' ? contentTestingPaths.de : contentTestingPaths.enUsd;
}

export function fromPriceLabel(currency: ContentTestingCurrency): string {
  return currency === 'EUR' ? '€99' : '$99';
}

export function withFromPrice(text: string, currency: ContentTestingCurrency): string {
  return text.replaceAll('{fromPrice}', fromPriceLabel(currency));
}

export function withFromPriceList(texts: string[], currency: ContentTestingCurrency): string[] {
  return texts.map((text) => withFromPrice(text, currency));
}
